/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * Report edit-mode action sequences — the ~would-be-handlers of the
 * prototype's admin mode (cardiac_report_v0_5_6_dev_2.html), each one the
 * exact rule sequence the prototype runs, expressed over the extracted core
 * modules. Like the core rule layer these MUTATE the report document in
 * place; callers re-render (and persist each save as a new immutable report
 * version) afterwards.
 *
 * Sequence provenance (prototype line refs):
 * - marker drag ticks/drop: onMarkerDrag L3243 / stopDraggingMarker L3269
 * - value save: saveEditedValue L6800
 * - Clinician Note blur commit: updateNoteToDoctor L4352-4390
 * - "Use suggested wording": applySuggestedNote L4675
 * - "Keep my note": confirmNoteStale L4446
 * - rewind: resetFeatureToBaseline L4288 (via core review.js)
 * - approve: approveAndIssueReport (via core review.js)
 *
 * Heart Report v0.5.8 (cardiac-panel-v4 git 12d10d7) made the patient-facing
 * copy product-controlled and gave every card a report-inclusion lifecycle:
 * the General Explanation and Patient Result Wording are no longer editable,
 * the Clinician Note starts empty and generated wording enters it only when
 * the clinician adopts it, and a card's Save / Cancel / Edit / Exclude /
 * Restore actions are the core inclusion rules, re-exported below.
 *
 * The Heart Rate card's range: either end can be dragged on the scale
 * (`applyMarkerDragTick` with an endpoint) or typed
 * (`applyHeartRateRangeEdit`); a typed single value replaces the range as the
 * clinician's assessment, with the accepted range kept for the audit record.
 *
 * Every timing card lists the recordings it includes (`toggleTimingSource`);
 * a SYS:DIA card's value spans the included recordings, and a typed value
 * or a moved marker replaces that range with the clinician's single value
 * (the range's endpoint markers go with it).
 */

import {
  CYCLE_REGULARITY_EDIT_HINT,
  STRUCTURAL_SOUND_STATUS_OBSERVED,
  SYS_DIA_DURATION_EDIT_HINT,
  approveAllFeatures,
  calculateMarkerPosition,
  cancelFeatureEdits as cancelFeatureAssessment,
  calculateObservedValueFromMarkerPosition,
  clearCycleTimingRange,
  currentHeartRateBpm,
  effectiveFeatureColorClass,
  formatHeartRateRange,
  getAutoProviderNote,
  getColorClass,
  getStatusFromMarkerPosition,
  heartRateRangeGrades,
  heartRateRangeMarkerPosition,
  heartRateRangeOf,
  isAssessmentUnset,
  isCardiacCycleTimingFeature,
  isHeartRateFeature,
  isRhythmRegularityFeature,
  isSysDiaDurationFeature,
  isTimingVariationText,
  parseLeadingNumber,
  applyStructuralSource,
  isCardEditable,
  noteClinicianModification,
  parseSdObservedRatioRange,
  parseSysDiaDurationObserved,
  parseSysDiaDurationRanges,
  providerCopyLooksAutoGenerated,
  refreshHeartRateDependentTimingCards,
  resetFeatureToBaseline,
  rhythmRegularitySwingLabel,
  rhythmRegularitySwingPct,
  rhythmRegularitySwingText,
  rhythmRegularityThresholds,
  structuralSoundResultLabel,
  syncNotesAfterStatusChange,
  timingPatternLabelForStatus,
} from "../core"

const isStructuralSoundFeature = (feature) =>
  feature.name === "Structural Sound" || feature.name === "Structural Sound Summary"

// Whether a card's notes were system-authored when a drag began, judged
// before the first tick changes the observed value (the generated provider
// text embeds it). Keyed by the live feature object; never persisted.
const preDragCopyState = new WeakMap()

// A reviewer-set result updates every field the card reads its state from,
// not only the colour: Cycle Regularity's percentage and pattern label, and
// Structural Sound's categorical observed text (the viewer's own labels) and
// result label.
const syncReviewerResultFields = (feature, status) => {
  feature.aiStatus = status === "green" ? "normal" : status === "red" ? "abnormal" : "borderline"
  if (isRhythmRegularityFeature(feature)) {
    feature.timingPatternLabel = timingPatternLabelForStatus(status)
    const swing = isTimingVariationText(feature.observed) ? rhythmRegularitySwingPct(feature.observed) : null
    if (swing !== null) {
      // The reviewer's result is a timing swing: it replaces the imported
      // one, and the measurement details that described the imported one
      // (its millisecond spread, its metric) follow it or go
      feature.timingSwingPct = swing
      feature.regularityPct = null
      const medianCycleMs = Number(feature.medianCycleMs)
      feature.timingSwingMs = Number.isFinite(medianCycleMs) && medianCycleMs > 0 ? (swing / 100) * medianCycleMs : null
      feature.metricUsed = null
    } else {
      feature.regularityPct = parseLeadingNumber(feature.observed)
    }
  }
  if (isStructuralSoundFeature(feature)) {
    // From the fresh observed text: the label read from the feature would
    // prefer the previous move's label over the text just written.
    feature.structuralSoundLabel = structuralSoundResultLabel({ observed: feature.observed }, status)
  }
}

export {
  beginAcceptedFeatureEdits,
  beginFeatureEdit,
  saveFeatureToReport,
  setFeatureReportInclusion,
  toggleTimingSource,
  undoFeatureReportInclusion,
} from "../core"

export const EMPTY_VALUE_HINT = "Enter an observed value, or cancel and set the assessment marker on the card."
export const HEART_RATE_RANGE_HINT =
  "Enter a valid minimum and maximum BPM. The minimum cannot be greater than the maximum."

const finiteRange = (range) =>
  !!range &&
  range.min !== null &&
  range.max !== null &&
  Number.isFinite(Number(range.min)) &&
  Number.isFinite(Number(range.max)) &&
  Number(range.min) < Number(range.max)

/**
 * The accepted heart-rate range behind a Heart Rate card, as the review
 * exported it — what the edit dialog names as the original evidence, kept
 * whatever the clinician replaces it with. Null for any other card or when
 * the accepted evidence was a single value.
 */
export function heartRateEvidenceRange(feature) {
  if (!isHeartRateFeature(feature)) return null
  const range = feature.sourceRangeBpm || feature.candidateEvidenceMeta?.rangeBpm
  return finiteRange(range) ? { min: Number(range.min), max: Number(range.max) } : null
}

/** The range the edit dialog opens with: the clinician's, else the evidence's. */
export function valueEditInitialRange(feature) {
  return heartRateRangeOf(feature) || heartRateEvidenceRange(feature)
}

// The card takes a range as its result: observed text, grade at each end,
// marker midway. The evidence range is kept the first time it is changed.
const setHeartRateRange = (features, feature, range) => {
  feature.sourceRangeBpm = feature.sourceRangeBpm || heartRateEvidenceRange(feature) || { ...range }
  feature.clinicianRangeBpm = { ...range }
  feature.clinicianAssessmentMode = "range"
  feature.observed = formatHeartRateRange(range)
  const grades = heartRateRangeGrades(range, feature.normalRange, feature.scaleType)
  feature.status = grades.status
  feature.mixedRange = grades.mixed
  feature.markerPosition = heartRateRangeMarkerPosition(feature, range, currentHeartRateBpm(features))
  feature.reviewTouched = true
  feature.manualAssessmentStarted = true
  feature.approved = false
  syncReviewerResultFields(feature, feature.status)
}

/**
 * A live drag tick (prototype onMarkerDrag): position + derived observed +
 * marker-authoritative grading, every tick. Cheap enough to run per move.
 * With an `endpoint` ("min" | "max") on a Heart Rate card that shows a range,
 * the tick moves that end of the range instead (upstream
 * startDraggingHeartRateRange / onMarkerDrag range branch); an end never
 * crosses the other.
 */
export function applyMarkerDragTick(features, index, percentage, endpoint) {
  const feature = features[index]
  const hrBpm = currentHeartRateBpm(features)
  if (!preDragCopyState.has(feature)) {
    preDragCopyState.set(feature, {
      provider: !feature.noteToDoctorEdited || providerCopyLooksAutoGenerated(feature, hrBpm),
    })
  }
  const clamped = Math.max(5, Math.min(95, percentage))
  const range = endpoint ? heartRateRangeOf(feature) : null
  if (range) {
    const bpm = parseLeadingNumber(calculateObservedValueFromMarkerPosition(feature, clamped, hrBpm))
    if (!Number.isFinite(bpm)) return
    const next =
      endpoint === "max"
        ? { min: range.min, max: Math.max(bpm, range.min) }
        : { min: Math.min(bpm, range.max), max: range.max }
    setHeartRateRange(features, feature, next)
    return
  }
  feature.markerPosition = clamped
  feature.reviewTouched = true
  // Moving the marker is an assessment: the card's neutral start is over
  feature.manualAssessmentStarted = true
  // One marker position is one value: a range derived from the included
  // recordings no longer describes the card
  clearCycleTimingRange(feature)
  feature.approved = false
  const status = getStatusFromMarkerPosition(clamped, feature.scaleType, feature, hrBpm)
  const observed = isStructuralSoundFeature(feature)
    ? STRUCTURAL_SOUND_STATUS_OBSERVED[status]
    : calculateObservedValueFromMarkerPosition(feature, clamped, hrBpm)
  if (observed !== null && observed !== undefined && !String(observed).includes("NaN")) {
    feature.observed = observed
  }
  feature.status = status
  syncReviewerResultFields(feature, status)
}

/**
 * Makes one of the Structural Sound card's accepted recordings its evidence
 * (upstream selectStructuralSource): the recording's scored result becomes
 * the card's value, as a clinician assessment, with the notes cascade a
 * value change gets. Refused, with the reason, outside an edit session or
 * for a recording that cannot support a result.
 */
export function selectStructuralSource(features, index, recordingId) {
  const feature = features[index]
  if (!feature || !isCardEditable(feature)) return { ok: false, reason: "Select Edit to change this assessment" }
  const result = applyStructuralSource(features, index, recordingId)
  if (!result.ok) return result
  feature.reviewTouched = true
  feature.manualAssessmentStarted = true
  feature.approved = false
  noteClinicianModification(feature)
  syncNotesAfterStatusChange(features, index)
  return result
}

/** Drag drop (prototype stopDraggingMarker): the post-move rule cascade. */
export function completeMarkerMove(features, index) {
  const feature = features[index]
  const copyWasAuto = preDragCopyState.get(feature) || {}
  preDragCopyState.delete(feature)
  // Only the card the clinician moved counts as clinician-modified; the
  // SYS:DIA cards re-derived from a new heart rate below do not (upstream
  // marks those too)
  noteClinicianModification(feature)
  syncNotesAfterStatusChange(features, index, { copyWasAuto })
  refreshHeartRateDependentTimingCards(features, feature)
}

/**
 * Why a typed value cannot be saved, or null when it can. An empty value is
 * never an observation; SYS:DIA Duration must keep both phase durations;
 * every other card accepts free text as upstream does.
 */
export function validateValueEdit(feature, value) {
  if (!String(value ?? "").trim()) return EMPTY_VALUE_HINT
  if (isSysDiaDurationFeature(feature)) {
    const parsed = parseSysDiaDurationObserved(value)
    if (!Number.isFinite(parsed.sysMs) || !Number.isFinite(parsed.diaMs)) return SYS_DIA_DURATION_EDIT_HINT
  }
  if (isRhythmRegularityFeature(feature) && typedTimingSwing(value) === null) return CYCLE_REGULARITY_EDIT_HINT
  return null
}

// The timing swing a Cycle Regularity entry names ("±6%", "6.0", "6% timing
// variation"), or null when it names none
const typedTimingSwing = (value) => {
  const number = parseLeadingNumber(String(value ?? "").replace(/^\s*±/, ""))
  return number === null || number < 0 ? null : number
}

// Each phase is one value or a range ("SYS 278–303 ms"); the typed pair
// replaces whatever the recordings gave. A range per phase stays the card's
// range, graded at both ends, with its midpoints as the representative
// values that place the marker.
const writeTypedDurations = (feature, typed) => {
  const ranges = parseSysDiaDurationRanges(typed)
  const phaseText = (low, high) =>
    Math.round(low) === Math.round(high) ? `${Math.round(low)} ms` : `${Math.round(low)}–${Math.round(high)} ms`
  const sysText = phaseText(ranges.sysMin, ranges.sysMax)
  const diaText = phaseText(ranges.diaMin, ranges.diaMax)
  feature.observed = `SYS ${sysText} · DIA ${diaText}`
  clearCycleTimingRange(feature)
  if (ranges.sysMin !== ranges.sysMax || ranges.diaMin !== ranges.diaMax) {
    feature.clinicianSysDiaDurationRange = { ...ranges }
    feature.representativeSysMs = (ranges.sysMin + ranges.sysMax) / 2
    feature.representativeDiaMs = (ranges.diaMin + ranges.diaMax) / 2
  }
}

// A typed swing is written in the card's wording with the band it falls in
// on the card's own thresholds
const writeTypedSwing = (features, feature, typed) => {
  const swing = typedTimingSwing(typed)
  const thresholds = rhythmRegularityThresholds(feature, currentHeartRateBpm(features))
  feature.observed = rhythmRegularitySwingText(swing, rhythmRegularitySwingLabel(swing, thresholds))
}

// Every other card takes the text as typed. A SYS:DIA Ratio range keeps its
// two ends as the card's range (a single value replaces the recordings');
// one Heart Rate value replaces the accepted range as the assessment, the
// range itself staying on the card for the audit record.
const writeTypedValue = (feature, typed) => {
  feature.observed = typed
  if (isCardiacCycleTimingFeature(feature)) {
    clearCycleTimingRange(feature)
    const typedRange = parseSdObservedRatioRange(typed)
    if (typedRange && typedRange.min < typedRange.max) feature.clinicianRatioRange = typedRange
  }
  if (heartRateEvidenceRange(feature)) {
    feature.sourceRangeBpm = feature.sourceRangeBpm || heartRateEvidenceRange(feature)
    feature.clinicianAssessmentMode = "single"
    feature.clinicianRangeBpm = null
    feature.mixedRange = false
  }
}

/**
 * Typed value save (prototype saveEditedValue). Returns false, changing
 * nothing, when `validateValueEdit` rejects the value.
 */
export function applyValueEdit(features, index, value) {
  const feature = features[index]
  if (validateValueEdit(feature, value)) return false
  const typed = String(value ?? "").trim()
  if (isSysDiaDurationFeature(feature)) writeTypedDurations(feature, typed)
  else if (isRhythmRegularityFeature(feature)) writeTypedSwing(features, feature, typed)
  else writeTypedValue(feature, typed)
  const hrBpm = currentHeartRateBpm(features)
  // The reviewer's value owns the marker from here on (imported timing
  // metrics no longer place it), so mark ownership before placing it.
  feature.reviewTouched = true
  feature.manualAssessmentStarted = true
  if (
    isRhythmRegularityFeature(feature) ||
    (isSysDiaDurationFeature(feature) && feature.clinicianSysDiaDurationRange)
  ) {
    // Grade the typed value on its own — Cycle Regularity from its swing's
    // band, a duration range at both ends — then place the marker from the
    // result rather than from the imported metrics.
    const status = getColorClass(
      feature.name,
      feature.observed,
      feature.normalRange,
      feature.status,
      feature.scaleType,
      hrBpm
    )
    syncReviewerResultFields(feature, status)
    feature.status = status
    feature.markerPosition = calculateMarkerPosition(feature, hrBpm)
  } else {
    feature.markerPosition = calculateMarkerPosition(feature, hrBpm)
    feature.status = getStatusFromMarkerPosition(feature.markerPosition, feature.scaleType, feature, hrBpm)
    syncReviewerResultFields(feature, feature.status)
  }
  feature.approved = false
  noteClinicianModification(feature)
  syncNotesAfterStatusChange(features, index)
  refreshHeartRateDependentTimingCards(features, feature)
  return true
}

/**
 * The value the edit dialog opens with: empty while the card has no
 * assessment yet. A Cycle Regularity card opens on its swing alone
 * ("±5.0%"): the band label in its value is derived, not typed.
 */
export function valueEditInitialText(feature) {
  if (isAssessmentUnset(feature)) return ""
  if (isRhythmRegularityFeature(feature) && isTimingVariationText(feature.observed)) {
    const swing = rhythmRegularitySwingPct(feature.observed)
    if (swing !== null) return `±${swing.toFixed(1)}%`
  }
  return String(feature.observed ?? "")
}

/** Why a typed range cannot be saved, or null when it can. */
export function validateHeartRateRangeEdit(min, max) {
  const lo = Number(min)
  const hi = Number(max)
  if (min === "" || max === "" || !Number.isFinite(lo) || !Number.isFinite(hi) || lo <= 0 || hi <= 0 || lo > hi) {
    return HEART_RATE_RANGE_HINT
  }
  return null
}

/**
 * Typed range save (upstream saveEditedValue, range branch): the card's
 * result becomes the range, graded at each end, with the same note cascade
 * as any value edit. Returns false, changing nothing, when the range is
 * refused.
 */
export function applyHeartRateRangeEdit(features, index, { min, max }) {
  const feature = features[index]
  if (!isHeartRateFeature(feature) || validateHeartRateRangeEdit(min, max)) return false
  setHeartRateRange(features, feature, { min: Number(min), max: Number(max) })
  noteClinicianModification(feature)
  syncNotesAfterStatusChange(features, index)
  refreshHeartRateDependentTimingCards(features, feature)
  return true
}

/**
 * Clinician Note blur commit (prototype updateNoteToDoctor). Returns false
 * when the value is unchanged (prototype early-return — no flags, no dirty
 * state). The note belongs to the clinician from here on.
 */
export function commitClinicianNote(features, index, value) {
  const feature = features[index]
  const current = feature.noteToDoctorEdited ? feature.noteToDoctor || "" : ""
  if (current === value) {
    return false
  }
  feature.noteToDoctor = value
  feature.noteToDoctorEdited = true
  feature.noteToDoctorStale = false
  feature.reviewTouched = true
  noteClinicianModification(feature)
  return true
}

/** The read-only "Suggested clinical interpretation" for the card's current result. */
export function suggestedClinicianNote(features, index) {
  const feature = features[index]
  const hrBpm = currentHeartRateBpm(features)
  return (
    getAutoProviderNote(
      feature.name,
      effectiveFeatureColorClass(feature, hrBpm),
      feature.observed,
      feature.displayReferenceRange || feature.normalRange,
      hrBpm
    ) || ""
  )
}

/**
 * "Use suggested wording" / "Use updated suggested note" (prototype
 * applySuggestedNote, doctor branch): copying the suggestion in is an explicit
 * clinician action, so the note counts as clinician-adopted. Until then
 * generated wording never appears as though the clinician wrote it.
 */
export function adoptSuggestedClinicianNote(features, index) {
  const feature = features[index]
  feature.noteToDoctor = suggestedClinicianNote(features, index) || feature.noteToDoctor
  feature.noteToDoctorEdited = true
  feature.noteToDoctorStale = false
  feature.reviewTouched = true
  noteClinicianModification(feature)
}

/** Stale-banner "Keep my note" (prototype confirmNoteStale): flag flip only. */
export function keepClinicianNote(features, index) {
  features[index].noteToDoctorStale = false
}

/**
 * Card Cancel (prototype cancelFeatureEdits). The SYS:DIA cards are graded
 * against the heart rate, so putting a Heart Rate card back re-derives them
 * too — otherwise they would stay graded against the abandoned value.
 */
export function cancelFeatureEdits(features, index) {
  const cancelled = cancelFeatureAssessment(features, index)
  if (cancelled) refreshHeartRateDependentTimingCards(features, features[index])
  return cancelled
}

/** Rewind (core resetFeatureToBaseline; false when no baseline captured). */
export function rewindFeature(features, index) {
  const rewound = resetFeatureToBaseline(features, index)
  if (rewound) refreshHeartRateDependentTimingCards(features, features[index])
  return rewound
}

/**
 * Approve (prototype approveAndIssueReport, via core approveAllFeatures):
 * blocked while a card in the report carries an unresolved Clinician Note,
 * while any card still needs Save or Exclude, or while no card is included.
 */
export function approveReport(features, options = {}) {
  return approveAllFeatures(features, options)
}
